/**
 * Camada de vídeo e rolagem em cima do <slideshow-component> do Dawn.
 *
 * O Dawn já cuida de dots, contador, autoplay, teclado e a11y. Aqui só
 * tratamos o que ele não conhece:
 *  - tocar o vídeo do slide ativo e pausar os demais
 *  - segurar a rotação enquanto o vídeo estiver tocando
 *  - o botão que rola para a próxima seção
 */
(() => {
  const setup = (slideshow) => {
    if (slideshow.dataset.baseSlideshowReady === 'true') return;
    slideshow.dataset.baseSlideshowReady = 'true';

    const slider = slideshow.querySelector('.slider');
    const slides = Array.from(slideshow.querySelectorAll('.base-slideshow__slide'));
    const pauseOnVideo = slideshow.dataset.pauseOnVideo === 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const videoOf = (slide) => {
      // Com mídia mobile e desktop existem dois <video>; só um está visível.
      const videos = Array.from(slide.querySelectorAll('video'));
      return videos.find((video) => video.offsetParent !== null) || videos[0] || null;
    };

    const activate = (index) => {
      slides.forEach((slide, position) => {
        const video = videoOf(slide);
        if (!video) return;

        if (position === index) {
          video.muted = true;
          const playPromise = video.play();
          if (playPromise) playPromise.catch(() => {});
        } else if (!video.paused) {
          video.pause();
          video.currentTime = 0;
        }
      });

      if (!pauseOnVideo || typeof slideshow.pause !== 'function') return;

      const activeHasVideo = slides[index]?.dataset.hasVideo === 'true';
      if (activeHasVideo) {
        slideshow.pause();
      } else if (typeof slideshow.play === 'function' && !reducedMotion) {
        slideshow.play();
      }
    };

    const isFade = slideshow.classList.contains('base-slideshow--fade');

    /**
     * Segura a troca enquanto a imagem que entra não estiver decodificada.
     *
     * Sem isso a transição roda no vazio: o slide de baixo já saiu, o de cima
     * ainda não tem pixel nenhum, e o que aparece é o fundo da régua até o
     * arquivo chegar. Em conexão ruim isso é um segundo de tela preta.
     */
    const holdUntilMediaReady = (slide) => {
      const image = slide?.querySelector('img');
      if (!image || image.complete) return;

      slideshow.setAttribute('data-fade-waiting', '');
      const release = () => slideshow.removeAttribute('data-fade-waiting');

      image.decode().then(release, release);
      // Nunca segurar o carrossel indefinidamente por causa de uma imagem.
      setTimeout(release, 3000);
    };

    slideshow.addEventListener('slideChanged', (event) => {
      // As animações de fade só entram em cena a partir da primeira troca real.
      // Armadas desde o começo, o primeiro slide entraria animado no load: meio
      // segundo de fundo preto antes do hero aparecer, bem no que costuma ser o
      // LCP da home. O evento chega antes do Dawn virar o aria-hidden, então a
      // própria transição que armou já sai animada.
      slideshow.setAttribute('data-fade-armed', '');

      const index = event.detail?.currentPage ? event.detail.currentPage - 1 : 0;
      if (isFade) holdUntilMediaReady(slides[index]);
      activate(index);
    });

    // O primeiro slide não dispara slideChanged: precisa ser iniciado à mão.
    if (slides.length) activate(0);

    // Sem esse guarda, o vídeo do slide continua tocando com a aba em segundo plano.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      slides.forEach((slide) => videoOf(slide)?.pause());
    });

    const scrollButton = slideshow.querySelector('[data-scroll-next]');
    if (scrollButton) {
      scrollButton.addEventListener('click', () => {
        const section = slideshow.closest('.shopify-section');
        const next = section?.nextElementSibling;
        if (!next) return;

        // Suave sempre: é o mesmo caso do sticky — navegação pedida no clique.
        next.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }

    if (slider) slider.setAttribute('data-base-slideshow', 'true');
  };

  const init = () => document.querySelectorAll('.base-slideshow').forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // O editor de tema re-renderiza a section a cada mudança de setting.
  document.addEventListener('shopify:section:load', init);
})();
